import * as THREE from 'three';
import type { CharacterRig } from './builders/character';
import { box, cyl, mat } from './materials';

export type LampKind = 'flashlight' | 'headlamp';

const SPECS: Record<LampKind, { intensity: number; angle: number; distance: number; beam: number }> = {
  flashlight: { intensity: 55, angle: 0.33, distance: 26, beam: 0.05 },
  headlamp: { intensity: 80, angle: 0.46, distance: 32, beam: 0.07 },
};

let lensMat: THREE.MeshStandardMaterial | null = null;
function lens(): THREE.MeshStandardMaterial {
  lensMat ??= new THREE.MeshStandardMaterial({
    color: '#fff8e0',
    emissive: new THREE.Color('#fff2c4'),
    emissiveIntensity: 2.2,
  });
  return lensMat;
}

/** The light on your forehead (or in your fist), on a character model. */
export function lampProp(kind: LampKind): THREE.Group {
  const g = new THREE.Group();
  if (kind === 'headlamp') {
    g.add(box(0.31, 0.04, 0.31, mat('#2a2a2a', { rough: 0.8 }), 0, 0, 0));
    g.add(box(0.11, 0.08, 0.06, mat('#d8d040', { rough: 0.5 }), 0, 0.01, 0.17));
    const l = cyl(0.03, 0.03, 0.01, lens(), 0, 0.01, 0.203, 10);
    l.rotation.x = Math.PI / 2;
    g.add(l);
    g.position.set(0, 0.25, 0);
  } else {
    // Held out in front at chest height, Mag-Lite style.
    const body = cyl(0.03, 0.03, 0.3, mat('#1b1b1e', { metal: 0.6, rough: 0.4 }), 0, 0, 0, 8);
    body.rotation.x = Math.PI / 2;
    g.add(body);
    const head = cyl(0.045, 0.035, 0.08, mat('#1b1b1e', { metal: 0.6, rough: 0.4 }), 0, 0, 0.18, 10);
    head.rotation.x = Math.PI / 2;
    g.add(head);
    const l = cyl(0.04, 0.04, 0.01, lens(), 0, 0, 0.222, 10);
    l.rotation.x = Math.PI / 2;
    g.add(l);
    g.position.set(0.2, 0.22, 0.24);
  }
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/** A soft cone of light in the air, so you can see the beam at night. */
export function beamCone(length = 7, radius = 2.2): THREE.Mesh {
  const geo = new THREE.ConeGeometry(radius, length, 20, 1, true);
  // Tip at the origin, opening toward -y... then pointed along +z.
  geo.translate(0, -length / 2, 0);
  geo.rotateX(-Math.PI / 2);
  const m = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({
      color: '#fff4cc',
      transparent: true,
      opacity: 0.06,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: true,
    }),
  );
  m.castShadow = m.receiveShadow = false;
  m.userData.noBatch = true;
  return m;
}

/**
 * Your flashlight or headlamp: a real spotlight that follows where you're looking,
 * the lamp on your character, and a faint beam in the night air.
 */
export class Lamp {
  readonly light = new THREE.SpotLight('#fff1d0', 0, 30, 0.4, 0.6, 1.1);
  private readonly beam = beamCone();
  private prop: THREE.Group | null = null;
  private kind: LampKind | null = null;
  private readonly tmp = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    // Always in the scene (switching lights in and out recompiles every material).
    this.light.castShadow = false;
    scene.add(this.light, this.light.target, this.beam);
    this.beam.visible = false;
  }

  get on(): LampKind | null {
    return this.kind;
  }

  /** Turns a lamp on (or off with null) and puts it on the character. */
  set(kind: LampKind | null, rig: CharacterRig): void {
    this.prop?.removeFromParent();
    this.prop = null;
    this.kind = kind;
    if (!kind) return;
    this.prop = lampProp(kind);
    (kind === 'headlamp' ? rig.head : rig.torso).add(this.prop);
    const s = SPECS[kind];
    this.light.angle = s.angle;
    this.light.distance = s.distance;
    (this.beam.material as THREE.MeshBasicMaterial).opacity = s.beam;
  }

  /** After the character's been rebuilt (new clothes), put the lamp back on. */
  reattach(rig: CharacterRig): void {
    if (this.kind) this.set(this.kind, rig);
  }

  /** `from` is where the light is, `dir` where it points. Brighter the darker it is out. */
  update(from: THREE.Vector3, dir: THREE.Vector3, darkness: number, showBeam: boolean): void {
    const k = this.kind;
    if (!k) {
      this.light.intensity = 0;
      this.beam.visible = false;
      return;
    }
    const s = SPECS[k];
    this.light.intensity = s.intensity * (0.25 + 0.75 * darkness);
    this.light.position.copy(from);
    this.light.target.position.copy(this.tmp.copy(from).addScaledVector(dir, 10));
    this.light.target.updateMatrixWorld();
    this.beam.visible = showBeam && darkness > 0.35;
    if (this.beam.visible) {
      this.beam.position.copy(from);
      this.beam.lookAt(this.tmp);
    }
  }
}

/** Three layers of heavy-duty foil, crimped into a point. */
export function foilHatProp(): THREE.Group {
  const g = new THREE.Group();
  const foil = mat('#d9dde2', { metal: 0.9, rough: 0.35, flat: true });
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.42, 7, 2), foil);
  cone.position.y = 0.5;
  cone.rotation.z = 0.12;
  const pos = cone.geometry.attributes.position as THREE.BufferAttribute;
  // Crinkle it.
  for (let i = 0; i < pos.count; i++) {
    const k = 1 + Math.sin(i * 12.9898) * 0.08;
    pos.setX(i, pos.getX(i) * k);
    pos.setZ(i, pos.getZ(i) * k);
  }
  cone.geometry.computeVertexNormals();
  cone.castShadow = true;
  g.add(cone);
  g.add(cyl(0.21, 0.21, 0.05, foil, 0, 0.31, 0, 9));
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}
